/**
 * Agency Portal section definitions — Slice 4 (§2.2).
 *
 * One `?view=agency&section=…` controls which panel the portal renders.
 * The legacy view ids (`clients`, `agencyReports`, `agencyReferral`,
 * `agencyWidget`, `whiteLabel`) alias into these sections so existing
 * deep links keep working.
 */

import type { View } from '../../app/view-router.js';

export const PORTAL_SECTIONS = [
  'overview',
  'clients',
  'reports',
  'leads',
  'referral',
  'widget',
  'white-label',
] as const;

export type PortalSection = (typeof PORTAL_SECTIONS)[number];

export const DEFAULT_SECTION: PortalSection = 'overview';

/** Legacy View id → portal section. Keeps `?view=clients` etc. working. */
export const LEGACY_VIEW_TO_SECTION: Readonly<Partial<Record<View, PortalSection>>> = {
  clients: 'clients',
  agencyReports: 'reports',
  agencyReferral: 'referral',
  agencyWidget: 'widget',
  whiteLabel: 'white-label',
};

export function parseSection(raw: string | null): PortalSection {
  if (raw === null) return DEFAULT_SECTION;
  return (PORTAL_SECTIONS as readonly string[]).includes(raw)
    ? (raw as PortalSection)
    : DEFAULT_SECTION;
}

export const SECTION_LABELS: Readonly<Record<PortalSection, string>> = {
  overview: 'Overview',
  clients: 'Clients',
  reports: 'Reports',
  leads: 'Leads',
  referral: 'Referral',
  widget: 'Scan widget',
  'white-label': 'White label',
};

export const SECTION_DESCRIPTIONS: Readonly<Record<PortalSection, string>> = {
  overview: 'Your agency at a glance. Numbers cover the last 30 days unless noted.',
  clients: 'Client accounts you can manage. Switching into a client shows their dashboard; your agency stays in the switcher.',
  reports: 'Scan prospects and clients, share the report link, or email it from Sill.',
  leads: 'People who scanned a site with your scan widget. Each scan creates a report.',
  referral: 'Share your link. Visitors who sign up through it are attributed to your agency.',
  widget: 'Put a site scanner on your own website. Each scan becomes a lead and a report.',
  'white-label': 'Your name, logo and colour on the reports and emails you send from Sill.',
};
